import { useContext, useEffect, useState } from 'react';
import { Button, Card, CardContent, CardMedia, CardProps, Grid, Typography, styled } from '@mui/material';
import { useNavigate } from 'react-router-dom';

import { useAppDispatch } from 'store/hooks';
import { checkDocumentStatus } from 'store/slices/documentSlice';
import { AuthContext } from 'utils/AuthContext';
import { ROUTE } from 'route/RouteEnums';
import { toast } from 'react-toastify';

interface DocumentStatusResponse {
  status: string;
  source: string;
  envelopId: string;
  signLink: string;
  templateId: string;
}

const StyleCard = styled(Card)<CardProps>(() => ({
  boxShadow: '0px 4px 16px 0px rgba(0, 0, 0, 0.12)',
  border: '1px solid #EFF4FF',
  borderRadius: 12,
  background: '#FFF',
  margin: '12px 16px',
  position: 'relative',
  zIndex: 99,
  '& .MuiTypography-root': {
    marginBottom: '8px',
    position: 'relative',
  },
}));

const DONE_STATUSES = ['COMPLETED', 'CONSENT COMPLETED'];

/**
 * Policies & Consents tile.
 * - On load: fetches CHECK_DOCUMENT_STATUS. If status is COMPLETED, or the
 *   patient has already acknowledged signing in-app (CONSENT COMPLETED), the
 *   tile is hidden entirely. Otherwise the tile is shown as a clickable prompt.
 * - On click: checks source. BOLDSIGN navigates to the dedicated in-app
 *   signing page; DOCUSIGN opens the signLink in a new tab (existing
 *   external-redirect flow, unchanged).
 */
const ConsentDocumentStatus = ({ profileStatus }: { profileStatus: string }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [documentStatus, setDocumentStatus] = useState<DocumentStatusResponse | null>(null);

  useEffect(() => {
    if (!user?.patientId) return;
    dispatch(checkDocumentStatus(user.patientId))
      .unwrap()
      .then((response: any) => {
        if (response?.status === 200) {
          setDocumentStatus(response?.data);
        }
      });
  }, [dispatch, user?.patientId]);

  if (!documentStatus || DONE_STATUSES.includes(documentStatus.status?.toUpperCase())) {
    return null;
  }

  const handleSignClick = () => {
    if (profileStatus !== 'COMPLETED' && profileStatus !== 'CONSENT COMPLETED') {
      toast('Please fill the profile info first', {
        type: 'info',
        icon: true,
      });
    }
    if (documentStatus.source?.toUpperCase() === 'BOLDSIGN') {
      navigate(ROUTE.OPEN_POLICY_DOCUMENTS, { state: { signLink: documentStatus.signLink } });
    }
  };

  return (
    <Grid item xs={12}>
      <StyleCard>
        <CardContent sx={{ position: 'relative' }}>
          <CardMedia
            component={'img'}
            src='images/Group 9550.png'
            alt='card-img'
            sx={{ width: 228, height: 'auto', position: 'absolute', right: 0, top: 0 }}
          />
          <Grid container spacing={1}>
            <Grid item xs={12} md={2}>
              <CardMedia
                component={'img'}
                src='images/consent.png'
                alt='policies and consents'
                sx={{ width: '97px', height: 'auto' }}
              />
            </Grid>
            <Grid item xs={12} md={6} mt={'10px'}>
              <Typography variant='h2'>Policies & Consents</Typography>
              <Typography variant='body1'>
                Review MindWeal&apos;s policies and provide the required acknowledgments and consents.
              </Typography>
            </Grid>
            <Grid item xs={12} md={4} mt={'25px'} textAlign={'end'}>
              <Button variant='outlined' onClick={handleSignClick}>
                Start Signing
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </StyleCard>
    </Grid>
  );
};

export default ConsentDocumentStatus;
